import { ArrowLeftIcon, WarningCircle } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { useCallback, useEffect, useState } from "react";
import { Button, Input } from "@/components/ui";
import { useTranslation } from "@/packages/utils";

export interface OtpVerificationFormProps {
  email: string;
  onSubmit: (code: string) => void;
  onResend: () => void;
  onBack: () => void;
  isLoading?: boolean;
  error?: string | null;
}

const RESEND_COOLDOWN_SECONDS = 60;

const stagger = {
  hidden: { opacity: 0, y: 10 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.06,
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94] as const,
    },
  }),
};

export function OtpVerificationForm({
  email,
  onSubmit,
  onResend,
  onBack,
  isLoading = false,
  error,
}: OtpVerificationFormProps) {
  const { t } = useTranslation(["auth"]);
  const [code, setCode] = useState("");
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (code.trim().length > 0) {
        onSubmit(code.trim());
      }
    },
    [code, onSubmit],
  );

  const handleResend = useCallback(() => {
    onResend();
    setCooldown(RESEND_COOLDOWN_SECONDS);
  }, [onResend]);

  return (
    <div className="flex flex-col">
      {/* Back link */}
      <motion.button
        type="button"
        onClick={onBack}
        custom={0}
        variants={stagger}
        initial="hidden"
        animate="show"
        className="flex items-center gap-1.5 text-[13px] text-secondary hover:text-primary transition-colors mb-8 self-start"
      >
        <ArrowLeftIcon className="size-3.5" />
        {t("auth:otpVerification.back")}
      </motion.button>

      {/* Header */}
      <motion.div custom={1} variants={stagger} initial="hidden" animate="show">
        <h1 className="text-[26px] font-bold tracking-[-0.02em] text-primary">
          {t("auth:otpVerification.title")}
        </h1>
        <p className="mt-2 text-[14px] text-secondary leading-relaxed">
          {t("auth:otpVerification.subtitle", { email })}
        </p>
      </motion.div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-5">
        {/* Code input */}
        <motion.div
          custom={2}
          variants={stagger}
          initial="hidden"
          animate="show"
          className="flex flex-col gap-1.5"
        >
          <label
            htmlFor="otp-code"
            className="text-[13px] font-medium text-secondary"
          >
            {t("auth:otpVerification.codeLabel")}
          </label>
          <Input
            id="otp-code"
            name="code"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={8}
            placeholder={t("auth:otpVerification.codePlaceholder")}
            required
            autoComplete="one-time-code"
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            className="text-center tracking-[0.3em] text-lg font-mono"
          />
        </motion.div>

        {/* Error */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            className="flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2.5"
          >
            <WarningCircle
              className="mt-0.5 size-4 shrink-0 text-red-500"
              weight="fill"
            />
            <p className="text-[13px] text-red-600">{error}</p>
          </motion.div>
        )}

        {/* Submit */}
        <motion.div
          custom={3}
          variants={stagger}
          initial="hidden"
          animate="show"
        >
          <Button
            type="submit"
            variant="primary"
            loading={isLoading}
            disabled={code.length === 0}
            className="w-full"
          >
            {t("auth:otpVerification.submit")}
          </Button>
        </motion.div>

        {/* Resend */}
        <motion.div
          custom={4}
          variants={stagger}
          initial="hidden"
          animate="show"
          className="text-center"
        >
          {cooldown > 0 ? (
            <p className="text-[13px] text-muted">
              {t("auth:otpVerification.resendCooldown", {
                seconds: cooldown,
              })}
            </p>
          ) : (
            <button
              type="button"
              onClick={handleResend}
              disabled={isLoading}
              className="text-[13px] font-medium text-secondary hover:text-primary transition-colors disabled:opacity-50"
            >
              {t("auth:otpVerification.resend")}
            </button>
          )}
        </motion.div>
      </form>
    </div>
  );
}
